import "./customerdisplay.scss";
import { type FC, useEffect, useState } from "react";
import { type CustomerDisplayComponentType } from "../../Routes/Settings/CustomerDisplay/CustomerDisplayTypes";
import { currency } from "../../Utilities/Generic/Numbers";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getOption, setOption } from "../../Utilities/Platform/Options/Options";
import { getScreenLabel } from "../../Utilities/Platform/WindowPlacement";
import { DISPLAY_FROM_SHOPFRONT } from "../../Workers/CustomerDisplay/DisplayEvents";
import DisplayWorker from "../../Workers/CustomerDisplay/DisplayWorker";
import LoadingScreen from "../Loading/LoadingScreen";
import CustomerDisplay, { type CustomerDisplayData } from "./CustomerDisplay";

type DisplayScreenType = "idle" | "sale";

const DEFAULT_DATA: CustomerDisplayData = {
    text: {
        itemCount: 0,
    },
    totals: {
        total    : currency(0),
        savings  : 0,
        cashout  : 0,
        change   : 0,
        remaining: 0,
        paid     : 0,
        surcharge: 0,
    },
    eftpos: false,
    sale  : [],
};

/**
 * Get the screen that the customer display should be full-screened on, attempts to restore the previous screen first
 * then selects the first monitor that is not the display with the sell screen on it
 */
async function getScreen(): Promise<null | ScreenDetailed> {
    if(typeof window.getScreenDetails !== "function") {
        return null;
    }

    const screenDetails = await window.getScreenDetails();

    if(screenDetails.screens.length <= 1) {
        return null;
    }

    const previous = getOption("_customer_display_previous");
    const sellScreen = getOption("_sell_screen_current_display");

    let selected: null | ScreenDetailed = null;

    for(const screen of screenDetails.screens) {
        const screenLabel = getScreenLabel(screen);

        // Skip the display the sell screen is currently on
        if(screenLabel === sellScreen) {
            continue;
        }

        // Select the first screen as the default, skip primary screen if sell screen isn't truthy
        if(selected === null && (!screen.isPrimary || !!sellScreen)) {
            selected = screen;

            // Check to see there is any point checking for the previous screen
            if(!previous) {
                break;
            }
        }

        if(screenLabel === previous) {
            selected = screen;
            break;
        }
    }

    return selected;
}

const CustomerDisplayWrapper: FC = () => {
    const [ fullscreenScreen, setFullscreenScreen ] = useState<null | ScreenDetailed>(null);
    const [ promptFullscreen, setPromptFullscreen ] = useState(false);
    const [ loadingScreen, setLoadingScreen ] = useState(true);

    const [ error, setError ] = useState<string>();
    const [ loading, setLoading ] = useState(true);
    const [ done, setDone ] = useState(false);
    const [ data, setData ] = useState({
        idle: DEFAULT_DATA,
        sale: DEFAULT_DATA,
    });
    const [ displays, setDisplays ] = useState<{
        idle: Array<CustomerDisplayComponentType>;
        sale: Array<CustomerDisplayComponentType>;
    }>({
        idle: [],
        sale: [],
    });

    useEffect(() => {
        let receiver = null;

        if(navigator.presentation && navigator.presentation.receiver) {
            receiver = navigator.presentation.receiver;
            setLoadingScreen(false);
        } else {
            // Get which screen the customer display should be put on
            getScreen()
                .then(screen => {
                    setFullscreenScreen(screen);
                    setPromptFullscreen(screen !== null);
                    setLoadingScreen(false);
                })
                .catch(err => {
                    console.error("Error getting customer display screen", err);
                    setPromptFullscreen(false);
                    setLoadingScreen(false);
                });
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const handleChange = (body: any): void => {
            const key = body.type === "current" ? "sale" : "idle";

            setDone(key === "idle");
            setData(prevData => ({
                ...prevData,
                [key]: body.sale,
            }));
        };

        const handleMessage = (type: DISPLAY_FROM_SHOPFRONT, body: any): void => {
            if(timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }

            switch(type) {
                case DISPLAY_FROM_SHOPFRONT.OPEN_DISPLAY:
                    setLoading(false);
                    setError(undefined);
                    setDisplays({
                        idle: JSON.parse(body.idle),
                        sale: JSON.parse(body.sale),
                    });
                    break;
                case DISPLAY_FROM_SHOPFRONT.DATA_CHANGE:
                    handleChange(body);
                    break;
                case DISPLAY_FROM_SHOPFRONT.DISPLAY_DONE:
                    setDone(true);
                    break;
            }
        };

        const worker = new DisplayWorker(receiver, handleMessage);

        timeoutId = setTimeout(() => {
            setError("timeout");
            setLoading(false);
            setDisplays({
                idle: [],
                sale: [],
            });
        }, 10000);

        return () => {
            if(process.env.NODE_ENV === "test") {
                worker.close(); // Closing will automatically be handled by the browser tab
            }
        };
    }, []);

    // Handle storing current screen effect after the customer display has been full-screened
    useEffect(() => {
        // Check to see if the current screen should be tracked
        if(!fullscreenScreen || promptFullscreen || loading || typeof error !== "undefined") {
            return;
        }

        if(typeof window.getScreenDetails !== "function") {
            return;
        }

        let details: null | ScreenDetails = null;
        let unmounted = false;

        const onScreenChange = (): void => {
            if(!details) {
                return;
            }

            setOption("_customer_display_previous", getScreenLabel(details.currentScreen));
        };

        window.getScreenDetails()
            .then(screenDetails => {
                if(!unmounted) {
                    setOption("_customer_display_previous", getScreenLabel(screenDetails.currentScreen));

                    details = screenDetails;

                    screenDetails.addEventListener("currentscreenchange", onScreenChange);
                }
            });

        return () => {
            unmounted = true;

            if(details) {
                details.removeEventListener("currentscreenchange", onScreenChange);
            }
        };
    }, [ fullscreenScreen, promptFullscreen, loading, error ]);

    let list: DisplayScreenType = "idle";

    if(data.sale.sale.length) {
        list = "sale";
    }

    if(error === "timeout") {
        return (
            <div className="customer-display fullscreen error">
                There was an issue trying to connect to the customer display!<br />
                Try closing and reopening the customer display
            </div>
        );
    }

    if(loading || loadingScreen) {
        return (
            <LoadingScreen>Loading Customer Display</LoadingScreen>
        );
    }

    if(promptFullscreen && fullscreenScreen) {
        const onFullscreen = async (): Promise<void> => {
            if(fullscreenScreen) {
                // Moves the window to the screen so that when
                // removed from fullscreen it is still on the correct monitor
                window.moveTo(fullscreenScreen.left, fullscreenScreen.top);

                await document.documentElement.requestFullscreen({
                    screen: fullscreenScreen,
                });
            }

            setPromptFullscreen(false);
        };

        return (
            <div className="w-screen h-screen p-16 dark:bg-dark-900">
                <button
                    onClick={onFullscreen}
                    className={
                        "button w-full h-full border-4 border-dashed text-2xl bg-white hover:bg-neutral-300 " +
                        "dark:border-dark-50 dark:text-dark-50 dark:bg-dark-900 dark:hover:bg-dark-700"
                    }
                >
                    <FontAwesomeLight icon="hand-pointer" /> Click here to open Customer Display
                </button>
            </div>
        );
    }

    return (
        <CustomerDisplay
            data={data[list]}
            format={displays[list]}
            done={done}
            fullscreen
        />
    );
};

export default CustomerDisplayWrapper;
